/**
 * Phoneme → IPA map for accurate spoken phoneme sounds via SSML.
 *
 * Isolated phonetic spellings ("aaah") are mispronounced by every TTS (Azure
 * renders short-A as /ɑː/ "aww" instead of /æ/). The fix is SSML
 * `<phoneme alphabet="ipa" ph="æ">a</phoneme>`, which makes Azure Neural produce
 * the exact sound. This map is the SINGLE SOURCE OF TRUTH shared by:
 *   - the app (web-child) — to send a structured pronunciation request,
 *   - the generator — to pre-bake the correct phoneme audio into the bundle,
 *   - media-api (BE) — to ALLOWLIST-validate `ipa`/`grapheme` before it builds
 *     and sends the SSML to Azure (the security boundary — never interpolate
 *     untrusted text into SSML markup; see docs / TTS security rule).
 *
 * IPA values are General American (the app voice is en-US-Ava). Keep the example
 * words aligned with the existing OG phoneme prompts (phonicsSpeech.ts).
 */
export interface PhonemeIpa {
  /** IPA symbol for SSML `<phoneme alphabet="ipa" ph="…">`. */
  ipa: string;
  /** The grapheme voiced inside the tag (also the visible letter). */
  grapheme: string;
  /** An example word that carries the sound, rendered normally for context. */
  example: string;
}

/** Keyed by the OG phoneme audioRef (e.g. block.audioRef === 'phoneme_short_a').
 *
 *  Short vowels (`phoneme_short_*`) drive the OG phoneme tiles. The single
 *  consonants (`phoneme_cons_*`) drive the Elkonin sound-box tiles (WordWorkStep):
 *  tapping a SOUND tile must play the letter SOUND (/h/, /dʒ/, /j/ …), never the
 *  letter NAME. c/g use their HARD (primary) sound — the sound in the vast
 *  majority of the decodable CVC words those boxes present (cat, cup / bag, dog),
 *  and the sound phonics introduces first. q/x are intentionally absent (q only
 *  occurs in the `qu` team; x = two sounds — the sound-box segmenter skips both).
 *
 *  IPA values are General American (the app voice is en-US-Ava). This map is a
 *  MIRROR: the media-api allowlist (src/apps/media-api/Program.cs `phonemeCodeMap`)
 *  and the generator's `PHONEME_CODES` (scripts/generate-tts-audio.mjs) MUST list
 *  the SAME codes, or a new sound is never baked (→ robotic/silent forever). */
export const PHONEME_IPA: Record<string, PhonemeIpa> = {
  phoneme_short_a: { ipa: 'æ', grapheme: 'a', example: 'cat' },
  phoneme_short_e: { ipa: 'ɛ', grapheme: 'e', example: 'bed' },
  phoneme_short_i: { ipa: 'ɪ', grapheme: 'i', example: 'fish' },
  phoneme_short_o: { ipa: 'ɑ', grapheme: 'o', example: 'hot' },
  phoneme_short_u: { ipa: 'ʌ', grapheme: 'u', example: 'cup' },
  // Single consonants — Elkonin sound-box tile sounds.
  phoneme_cons_b: { ipa: 'b', grapheme: 'b', example: 'bat' },
  phoneme_cons_c: { ipa: 'k', grapheme: 'c', example: 'cat' },
  phoneme_cons_d: { ipa: 'd', grapheme: 'd', example: 'dog' },
  phoneme_cons_f: { ipa: 'f', grapheme: 'f', example: 'fan' },
  phoneme_cons_g: { ipa: 'ɡ', grapheme: 'g', example: 'goat' },
  phoneme_cons_h: { ipa: 'h', grapheme: 'h', example: 'hat' },
  phoneme_cons_j: { ipa: 'dʒ', grapheme: 'j', example: 'jam' },
  phoneme_cons_k: { ipa: 'k', grapheme: 'k', example: 'kite' },
  phoneme_cons_l: { ipa: 'l', grapheme: 'l', example: 'leg' },
  phoneme_cons_m: { ipa: 'm', grapheme: 'm', example: 'map' },
  phoneme_cons_n: { ipa: 'n', grapheme: 'n', example: 'net' },
  phoneme_cons_p: { ipa: 'p', grapheme: 'p', example: 'pig' },
  phoneme_cons_r: { ipa: 'ɹ', grapheme: 'r', example: 'red' },
  phoneme_cons_s: { ipa: 's', grapheme: 's', example: 'sun' },
  phoneme_cons_t: { ipa: 't', grapheme: 't', example: 'top' },
  phoneme_cons_v: { ipa: 'v', grapheme: 'v', example: 'van' },
  phoneme_cons_w: { ipa: 'w', grapheme: 'w', example: 'wet' },
  phoneme_cons_y: { ipa: 'j', grapheme: 'y', example: 'yes' },
  phoneme_cons_z: { ipa: 'z', grapheme: 'z', example: 'zip' },
  // Consonant digraphs (`phoneme_digraph_*`) — the island digraph popup fix
  // (2026-07: raw "sh" text was read as "sheh"). Two letters, ONE sound: the
  // SSML tag voices the whole grapheme team with its single phoneme. th uses
  // the unvoiced /θ/ (think) — the sound phonics introduces first; ck is the
  // same /k/ sound as c/k spelt with the two-letter team.
  phoneme_digraph_sh: { ipa: 'ʃ', grapheme: 'sh', example: 'ship' },
  phoneme_digraph_th: { ipa: 'θ', grapheme: 'th', example: 'think' },
  phoneme_digraph_ch: { ipa: 'tʃ', grapheme: 'ch', example: 'chimp' },
  phoneme_digraph_wh: { ipa: 'w', grapheme: 'wh', example: 'whale' },
  phoneme_digraph_ck: { ipa: 'k', grapheme: 'ck', example: 'duck' },
  phoneme_digraph_ng: { ipa: 'ŋ', grapheme: 'ng', example: 'ring' },
};

/**
 * Single letter (a sound-box tile's grapheme) → its phoneme audio code, or null
 * when the letter has no single isolated sound we bake (q, x — see PHONEME_IPA).
 * Short vowels reuse the OG `phoneme_short_*` clips; consonants use `phoneme_cons_*`.
 * Used by the Elkonin sound-box step so a SOUND tile plays the SOUND, not the name.
 */
const LETTER_TO_PHONEME_CODE: Record<string, string> = {
  a: 'phoneme_short_a', e: 'phoneme_short_e', i: 'phoneme_short_i',
  o: 'phoneme_short_o', u: 'phoneme_short_u',
  b: 'phoneme_cons_b', c: 'phoneme_cons_c', d: 'phoneme_cons_d', f: 'phoneme_cons_f',
  g: 'phoneme_cons_g', h: 'phoneme_cons_h', j: 'phoneme_cons_j', k: 'phoneme_cons_k',
  l: 'phoneme_cons_l', m: 'phoneme_cons_m', n: 'phoneme_cons_n', p: 'phoneme_cons_p',
  r: 'phoneme_cons_r', s: 'phoneme_cons_s', t: 'phoneme_cons_t', v: 'phoneme_cons_v',
  w: 'phoneme_cons_w', y: 'phoneme_cons_y', z: 'phoneme_cons_z',
};

/** The phoneme code for a single-letter sound tile, or null (multi-letter unit,
 *  or a letter with no isolated single-sound clip — q/x). */
export function letterPhonemeCode(letter: string | undefined | null): string | null {
  if (!letter || letter.length !== 1) return null;
  return LETTER_TO_PHONEME_CODE[letter.toLowerCase()] ?? null;
}

/** Structured pronunciation for an audioRef, or null if it isn't a mapped phoneme. */
export function getPhonemeIpa(audioRef: string | undefined | null): PhonemeIpa | null {
  if (!audioRef) return null;
  return PHONEME_IPA[audioRef] ?? null;
}

/**
 * Allowlists for validating an SSML phoneme request — used identically on the
 * client and (mirrored) on media-api. A request whose `ipa`/`grapheme` is not in
 * these sets must be rejected, never interpolated into markup.
 */
export const ALLOWED_PHONEME_IPA: readonly string[] = Object.freeze(
  [...new Set(Object.values(PHONEME_IPA).map((p) => p.ipa))],
);
export const ALLOWED_PHONEME_GRAPHEMES: readonly string[] = Object.freeze(
  [...new Set(Object.values(PHONEME_IPA).map((p) => p.grapheme))],
);

/** True if a {ipa, grapheme} pair is a known, allowlisted phoneme. */
export function isAllowedPhoneme(ipa: string, grapheme: string): boolean {
  return ALLOWED_PHONEME_IPA.includes(ipa) && ALLOWED_PHONEME_GRAPHEMES.includes(grapheme);
}

/**
 * Canonical keying string for a phoneme's SSML audio. Phoneme audio is keyed by
 * this (NOT by the prompt text) so the app's prebaked/device-cache lookup and the
 * generator agree, and so the correct /æ/ clip never collides with the old
 * plain-text "aaah" clip. MUST stay byte-identical to the generator's copy in
 * scripts/generate-tts-audio.mjs. Trim-safe; won't appear in real spoken text.
 */
export function phonemeKeyText(code: string): string {
  return `[[phoneme:${code}]]`;
}
